import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeftRight, ArrowRight, Banknote, CheckCircle,
  CreditCard, Clock, Loader2, Package, Plus, Search,
  SlidersHorizontal, Trash2, X, AlertTriangle, CalendarDays,
  Receipt, Zap, History,
} from "lucide-react";
import {
  loadPacksAndTariffs as loadPacksAndTariffsFn,
  calculateTariffPrice,
  getPackPrice,
} from "@/lib/tariffCalculator";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ─────────────────────────────────────────────────────────────────────────
   Helper: get working price for an item given new days
   ───────────────────────────────────────────────────────────────────────── */
function calcWorkingPrice(item, newDays, allWorkingItems, storePacks, storeTariffs, originalRentalDays) {
  if (!newDays || newDays <= 0) return item.unit_price;
  const origDays = item.item_days || originalRentalDays || 1;

  // Pack item (not broken)
  if (item.pack_id && item.pack_instance_id && !item.packBroken) {
    const pack = storePacks.find((p) => p.id === item.pack_id);
    if (pack) {
      const sibItems = allWorkingItems.filter(
        (i) => i.pack_instance_id === item.pack_instance_id && !i.returned
      );
      const totalOldPrice = sibItems.reduce((s, i) => s + i.unit_price, 0);
      const pricePaid = getPackPrice(pack, origDays) || totalOldPrice;
      const priceNew = getPackPrice(pack, newDays);
      const ratio = pricePaid > 0 ? totalOldPrice / pricePaid : 1;
      const share = totalOldPrice > 0 ? item.unit_price / totalOldPrice : 1 / sibItems.length;
      return Math.max(0, priceNew * ratio * share);
    }
  }

  // Individual item
  const tariffOld = calculateTariffPrice([item.item_type], origDays, storePacks, storeTariffs);
  const tariffNew = calculateTariffPrice([item.item_type], newDays, storePacks, storeTariffs);
  if (tariffOld > 0) {
    const ratio = item.unit_price / tariffOld;
    return Math.max(0, tariffNew * ratio);
  }
  // Proportional fallback
  return Math.max(0, (item.unit_price / origDays) * newDays);
}

/* ─────────────────────────────────────────────────────────────────────────
   Main Component
   ───────────────────────────────────────────────────────────────────────── */
export function AjustesDashboard({ rental: initialRental, isOpen, onClose, onSuccess }) {
  // ── Store data ──────────────────────────────────────────────────────────
  const [storePacks, setStorePacks] = useState([]);
  const [storeTariffs, setStoreTariffs] = useState([]);

  // ── Working invoice ─────────────────────────────────────────────────────
  const [workingItems, setWorkingItems] = useState([]);
  const [newEndDate, setNewEndDate] = useState("");

  // ── Staged additions ────────────────────────────────────────────────────
  const [stagedAdds, setStagedAdds] = useState([]);
  // ── Temp basket (pre-commit, right panel) ───────────────────────────────
  const [tempBasket, setTempBasket] = useState([]);

  // ── Staged supplements ──────────────────────────────────────────────────
  const [supplements, setSupplements] = useState([]);
  const supplementsTotal = useMemo(
    () => supplements.reduce((s, x) => s + (x.amount || 0), 0),
    [supplements]
  );

  // ── CAMBIOS block ───────────────────────────────────────────────────────
  const [swapFromCode, setSwapFromCode] = useState("");
  const [swapToCode, setSwapToCode] = useState("");
  const [swapLoading, setSwapLoading] = useState(false);
  const swapToRef = useRef(null);

  // ── AÑADIR block ────────────────────────────────────────────────────────
  const [addSearch, setAddSearch] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [addLoading, setAddLoading] = useState(false);
  const [addDays, setAddDays] = useState(0);
  const addSearchRef = useRef(null);

  // ── SUPLEMENTO block ────────────────────────────────────────────────────
  const [supplAmount, setSupplAmount] = useState("");
  const [supplComment, setSupplComment] = useState("");

  // ── Confirmation ────────────────────────────────────────────────────────
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [confirming, setConfirming] = useState(false);
  const [step, setStep] = useState("editing"); // editing | payment | processing | done

  // ── Historial ────────────────────────────────────────────────────────────
  const [showHistorial, setShowHistorial] = useState(false);
  const [historialEvents, setHistorialEvents] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);

  // ── Current rental (refreshed after operations) ─────────────────────────
  const [rental, setRental] = useState(initialRental);

  // Sync prop → state when prop changes (component is always mounted, rental starts null)
  useEffect(() => {
    if (initialRental) setRental(initialRental);
  }, [initialRental]);

  // ──────────────────────────────────────────────────────────────────────────
  // Initialize on open
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !rental) return;

    // Load packs & tariffs
    loadPacksAndTariffsFn(API, {}).then(({ packs, tariffs }) => {
      setStorePacks(packs);
      setStoreTariffs(tariffs);
    });

    // Init working items from rental — each item gets a unique _uid (index-based)
    // so two items with the same barcode (generic items) are treated independently.
    const items = (rental.items || []).filter((i) => !i.returned).map((i, idx) => ({
      ...i,
      _uid: `${i.barcode}-${idx}`,        // guaranteed unique even when barcodes repeat
      working_days: i.item_days || rental.days || 1,
      working_price: i.unit_price || 0,
      isSwapped: false,
      swapNewItem: null,
      swap_delta: 0,
      packBroken: false,
    }));
    setWorkingItems(items);

    // Default end date
    setNewEndDate(rental.end_date ? rental.end_date.split("T")[0] : "");
    setAddDays(rental.days || 1);

    // Reset staged
    setStagedAdds([]);
    setTempBasket([]);
    setSupplements([]);
    setShowHistorial(false);
    setHistorialEvents([]);
    setSwapFromCode("");
    setSwapToCode("");
    setAddSearch("");
    setAddResults([]);
    setStep("editing");
  }, [isOpen, rental]);

  // ──────────────────────────────────────────────────────────────────────────
  // Derived: grouped invoice rows
  // ──────────────────────────────────────────────────────────────────────────
  const invoiceRows = useMemo(() => {
    const seenPacks = new Set();
    const rows = [];

    // Build pack instance groups
    const packInstances = {};
    workingItems.forEach((item) => {
      if (item.pack_instance_id) {
        if (!packInstances[item.pack_instance_id])
          packInstances[item.pack_instance_id] = [];
        packInstances[item.pack_instance_id].push(item);
      }
    });

    // First: pack groups (only groups with 2+ members)
    const processedInstances = new Set();
    workingItems.forEach((item) => {
      const pid = item.pack_instance_id;
      if (pid && !processedInstances.has(pid) && (packInstances[pid]?.length || 0) >= 2) {
        processedInstances.add(pid);
        seenPacks.add(pid);
        rows.push({ type: "pack", packInstanceId: pid, items: packInstances[pid] });
      }
    });

    // Then: individual items (not in valid pack groups)
    workingItems.forEach((item) => {
      if (!item.pack_instance_id || !seenPacks.has(item.pack_instance_id)) {
        rows.push({ type: "item", item });
      }
    });

    return rows;
  }, [workingItems]);

  // ──────────────────────────────────────────────────────────────────────────
  // Financial calculations
  // ──────────────────────────────────────────────────────────────────────────
  const originalTotal = rental?.total_amount || 0;

  const diff = useMemo(() => {
    let d = 0;
    // Original items – price change from day edits or swaps
    workingItems.forEach((item) => {
      if (item.isSwapped) {
        d += item.swap_delta;
      } else {
        d += item.working_price - item.unit_price;
      }
    });
    // Added items
    stagedAdds.forEach((i) => { d += i.working_price; });
    // Supplements
    supplements.forEach((s) => { d += s.amount; });
    return d;
  }, [workingItems, stagedAdds, supplements]);

  const hasPendingChanges = useMemo(() => {
    const hasSwaps = workingItems.some((i) => i.isSwapped);
    const hasDateChange = newEndDate && newEndDate !== (rental?.end_date || "").split("T")[0];
    const hasItemDayChanges = workingItems.some((i) => i.working_days !== i.item_days);
    // Also detect when prices changed (e.g. days stayed the same but tariff recalculation
    // produced a different working_price — happens when item_days in DB ≠ actual days state)
    const hasPriceChanges = workingItems.some(
      (i) => !i.isSwapped && Math.abs((i.working_price || 0) - (i.unit_price || 0)) > 0.01
    );
    return hasSwaps || hasDateChange || hasItemDayChanges || hasPriceChanges || stagedAdds.length > 0 || supplements.length > 0;
  }, [workingItems, newEndDate, rental, stagedAdds, supplements]);

  // ──────────────────────────────────────────────────────────────────────────
  // Global date change → update all items' working days & prices
  // ──────────────────────────────────────────────────────────────────────────
  const handleGlobalEndDateChange = useCallback(
    (newDate) => {
      setNewEndDate(newDate);
      if (!newDate || !rental) return;

      const start = new Date(rental.start_date);
      start.setHours(0, 0, 0, 0);
      const end = new Date(newDate + "T00:00:00");
      const newDays = Math.max(1, Math.round((end - start) / 86400000) + 1);

      setAddDays(newDays);
      setWorkingItems((prev) =>
        prev.map((item) => {
          if (item.isSwapped) return item;
          const newPrice = calcWorkingPrice(item, newDays, prev, storePacks, storeTariffs, rental.days);
          return { ...item, working_days: newDays, working_price: newPrice };
        })
      );
    },
    [rental, storePacks, storeTariffs]
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Per-item / per-pack days change
  // ──────────────────────────────────────────────────────────────────────────
  const handleItemDaysChange = useCallback(
    (uid, newDays) => {
      const days = parseInt(newDays, 10);
      if (!days || days < 1) return;
      setWorkingItems((prev) =>
        prev.map((item) => {
          if (item._uid !== uid) return item;   // ← _uid is unique even for repeated barcodes
          const newPrice = calcWorkingPrice(item, days, prev, storePacks, storeTariffs, rental?.days || 1);
          return { ...item, working_days: days, working_price: newPrice };
        })
      );
    },
    [storePacks, storeTariffs, rental]
  );

  const handlePackDaysChange = useCallback(
    (packInstanceId, newDays) => {
      const days = parseInt(newDays, 10);
      if (!days || days < 1) return;
      setWorkingItems((prev) => {
        const packItems = prev.filter((i) => i.pack_instance_id === packInstanceId);
        const totalOldPrice = packItems.reduce((s, i) => s + i.unit_price, 0);
        const pack = storePacks.find((p) => p.id === packItems[0]?.pack_id);
        let priceNew = 0;
        if (pack) {
          const origDays = packItems[0]?.item_days || rental?.days || 1;
          const pricePaid = getPackPrice(pack, origDays) || totalOldPrice;
          const ratio = pricePaid > 0 ? totalOldPrice / pricePaid : 1;
          priceNew = getPackPrice(pack, days) * ratio;
        } else {
          const origDays = packItems[0]?.item_days || rental?.days || 1;
          priceNew = totalOldPrice * (days / origDays);
        }
        return prev.map((item) => {
          if (item.pack_instance_id !== packInstanceId) return item;
          const share = totalOldPrice > 0 ? item.unit_price / totalOldPrice : 1 / packItems.length;
          return {
            ...item,
            working_days: days,
            working_price: Math.max(0, priceNew * share),
          };
        });
      });
    },
    [storePacks, rental]
  );

  // ──────────────────────────────────────────────────────────────────────────
  // CAMBIOS: Execute Swap
  // ──────────────────────────────────────────────────────────────────────────
  const executeSwap = useCallback(async () => {
    if (!swapFromCode.trim() || !swapToCode.trim()) {
      toast.error("Introduce ambos códigos para realizar el cambio");
      return;
    }
    setSwapLoading(true);
    try {
      // Find the "from" item in workingItems
      const fromItem = workingItems.find(
        (i) =>
          i.barcode?.toUpperCase() === swapFromCode.toUpperCase() ||
          i.internal_code?.toUpperCase() === swapFromCode.toUpperCase()
      );
      if (!fromItem) {
        toast.error(`Artículo "${swapFromCode}" no encontrado en este alquiler`);
        return;
      }
      if (fromItem.isSwapped) {
        toast.error("Este artículo ya está pendiente de cambio");
        return;
      }

      // Look up the new item
      const resp = await axios.get(`${API}/items?search=${encodeURIComponent(swapToCode)}`);
      const items = resp.data;
      const toItem = items.find(
        (i) =>
          i.barcode?.toUpperCase() === swapToCode.toUpperCase() ||
          i.internal_code?.toUpperCase() === swapToCode.toUpperCase()
      );
      if (!toItem) {
        toast.error(`Artículo "${swapToCode}" no encontrado`);
        return;
      }
      if (toItem.status === "rented") {
        toast.error("Ese artículo ya está alquilado");
        return;
      }
      if (!["available", "dirty"].includes(toItem.status)) {
        toast.error(`Artículo no disponible (${toItem.status})`);
        return;
      }

      // Calculate delta
      const daysRemaining = Math.max(1, fromItem.working_days);
      const dayField = daysRemaining <= 10 ? `day_${daysRemaining}` : "day_11_plus";
      const oldType = fromItem.item_type?.toLowerCase().trim();
      const newType = toItem.item_type?.toLowerCase().trim();
      const isPackBreak = !!(fromItem.pack_id && fromItem.pack_instance_id && oldType !== newType);

      let delta = 0;
      if (isPackBreak) {
        const pack = storePacks.find((p) => p.id === fromItem.pack_id);
        if (pack) {
          const packPriceRem = getPackPrice(pack, daysRemaining);
          const siblings = workingItems.filter(
            (i) =>
              i.pack_instance_id === fromItem.pack_instance_id &&
              i._uid !== fromItem._uid &&
              !i.isSwapped
          );
          const sibTotal = siblings.reduce((s, sib) => {
            const sibTariff = storeTariffs.find(
              (t) => t.item_type?.toLowerCase() === sib.item_type?.toLowerCase()
            );
            return s + (sibTariff?.[dayField] || 0);
          }, 0);
          const newTariff = storeTariffs.find((t) => t.item_type?.toLowerCase() === newType);
          const newPrice = newTariff?.[dayField] || 0;
          delta = newPrice + sibTotal - packPriceRem;
        }
      } else if (oldType === newType) {
        delta = 0; // technical swap
      } else {
        const oldTariff = storeTariffs.find((t) => t.item_type?.toLowerCase() === oldType);
        const newTariff = storeTariffs.find((t) => t.item_type?.toLowerCase() === newType);
        const oldPrice = oldTariff?.[dayField] || fromItem.unit_price || 0;
        const newPrice = newTariff?.[dayField] || 0;
        delta = newPrice - oldPrice;
      }

      // Stage the swap
      setWorkingItems((prev) => {
        const updated = prev.map((item) => {
          if (item._uid === fromItem._uid) {
            return {
              ...item,
              isSwapped: true,
              swapNewItem: toItem,
              swap_delta: Math.round(delta * 100) / 100,
              packBroken: isPackBreak,
            };
          }
          // Mark pack siblings as pack-broken (visual warning)
          if (isPackBreak && item.pack_instance_id === fromItem.pack_instance_id) {
            return { ...item, packBroken: true };
          }
          return item;
        });
        return updated;
      });

      const label =
        delta > 0 ? "⬆️ Upgrade" : delta < 0 ? "⬇️ Downgrade" : "🔄 Cambio técnico";
      toast.success(
        `${label}: ${fromItem.internal_code || fromItem.barcode} → ${toItem.internal_code || toItem.barcode}${isPackBreak ? " · Pack roto" : ""}`
      );
      setSwapFromCode("");
      setSwapToCode("");
    } catch {
      toast.error("Error al buscar artículo");
    } finally {
      setSwapLoading(false);
    }
  }, [swapFromCode, swapToCode, workingItems, storePacks, storeTariffs]);

  const cancelSwap = (uid) => {
    setWorkingItems((prev) =>
      prev.map((item) => {
        if (item._uid !== uid) return item;
        return { ...item, isSwapped: false, swapNewItem: null, swap_delta: 0, packBroken: false };
      })
    );
  };

  // ──────────────────────────────────────────────────────────────────────────
  // AÑADIR: Search and stage new items
  // ──────────────────────────────────────────────────────────────────────────
  const searchAddItems = useCallback(
    async (query) => {
      if (!query || query.length < 2) { setAddResults([]); return; }
      setAddLoading(true);
      try {
        const resp = await axios.get(
          `${API}/items?status=available&search=${encodeURIComponent(query)}&limit=8`
        );
        const existingBarcodes = new Set([
          ...workingItems.map((i) => i.barcode),
          ...stagedAdds.map((i) => i.barcode),
          ...tempBasket.map((i) => i.barcode),
        ]);
        setAddResults(resp.data.filter((i) => !existingBarcodes.has(i.barcode)).slice(0, 8));
      } catch {
        toast.error("Error al buscar artículos");
      } finally {
        setAddLoading(false);
      }
    },
    [workingItems, stagedAdds, tempBasket]
  );

  // ── Load historial ──────────────────────────────────────────────────────
  const loadHistorial = useCallback(async () => {
    if (!rental?.id) return;
    setHistorialLoading(true);
    try {
      const res = await axios.get(`${API}/rentals/${rental.id}/history`);
      setHistorialEvents(res.data.events || []);
    } catch {
      setHistorialEvents([]);
    } finally {
      setHistorialLoading(false);
    }
  }, [rental?.id]);

  const toggleHistorial = useCallback(() => {
    setShowHistorial((v) => {
      if (!v) loadHistorial();
      return !v;
    });
  }, [loadHistorial]);

  const addToTempBasket = useCallback(    (item) => {
      setTempBasket((prev) => {
        if (prev.some((i) => i.barcode === item.barcode)) return prev;
        return [...prev, item];
      });
      setAddSearch("");
      setAddResults([]);
      toast.success(`${item.internal_code || item.barcode} → cesta temporal`);
    },
    []
  );

  const removeFromTempBasket = (barcode) => {
    setTempBasket((prev) => prev.filter((i) => i.barcode !== barcode));
  };

  // ── LISTO: Pack detection + tariff pricing → volcado a Nota Original ────
  const commitBasket = useCallback(() => {
    if (tempBasket.length === 0) return;

    const days = addDays;
    const result = [];
    const usedIndices = new Set();

    // ── Paso A: Detección de Packs ────────────────────────────────────────
    // Ordenar packs de mayor a menor (priorizar packs más grandes)
    const sortedPacks = [...storePacks].sort((a, b) => (b.items?.length || 0) - (a.items?.length || 0));

    for (const pack of sortedPacks) {
      if (!pack.items || pack.items.length === 0) continue;
      const requiredTypes = pack.items.map((t) => t.toLowerCase());

      // Intentar formar tantas instancias de este pack como sea posible
      let keepForming = true;
      while (keepForming) {
        const matchedItems = [];
        const matchedIndices = [];

        for (const reqType of requiredTypes) {
          let found = false;
          for (let i = 0; i < tempBasket.length; i++) {
            if (
              !usedIndices.has(i) &&
              !matchedIndices.includes(i) &&
              tempBasket[i].item_type?.toLowerCase() === reqType
            ) {
              matchedItems.push(tempBasket[i]);
              matchedIndices.push(i);
              found = true;
              break;
            }
          }
          if (!found) { keepForming = false; break; }
        }

        if (keepForming && matchedItems.length === requiredTypes.length) {
          const packInstanceId = `new-${pack.id.slice(0, 8)}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
          const packPrice = getPackPrice(pack, days) || 0;

          // ── Paso B: Distribuir precio del pack proporcionalmente por tarifa individual
          const tariffPrices = matchedItems.map((item) =>
            calculateTariffPrice([item.item_type], days, [], storeTariffs)
          );
          const totalTariff = tariffPrices.reduce((s, p) => s + p, 0);

          matchedItems.forEach((item, idx) => {
            const share = totalTariff > 0 ? tariffPrices[idx] / totalTariff : 1 / matchedItems.length;
            result.push({
              ...item,
              pack_id: pack.id,
              pack_instance_id: packInstanceId,
              pack_name: pack.name,
              working_days: days,
              working_price: Math.round(packPrice * share * 100) / 100,
            });
            usedIndices.add(matchedIndices[idx]);
          });
        }
      }
    }

    // ── Paso B (artículos sueltos): tarifa individual ─────────────────────
    tempBasket.forEach((item, i) => {
      if (!usedIndices.has(i)) {
        const price = calculateTariffPrice([item.item_type], days, storePacks, storeTariffs);
        result.push({
          ...item,
          working_days: days,
          working_price: price > 0 ? price : (item.rental_price || 0),
        });
      }
    });

    // ── Paso C: Volcar a Nota Original y limpiar cesta ────────────────────
    setStagedAdds((prev) => [...prev, ...result]);
    setTempBasket([]);

    const packInstances = new Set(result.filter((i) => i.pack_instance_id).map((i) => i.pack_instance_id));
    const packCount = packInstances.size;
    const looseCount = result.filter((i) => !i.pack_instance_id).length;

    if (packCount > 0 && looseCount > 0) {
      toast.success(`${packCount} pack(s) y ${looseCount} artículo(s) suelto(s) añadidos a la nota`);
    } else if (packCount > 0) {
      toast.success(`${packCount} pack(s) añadidos — tarifas aplicadas correctamente`);
    } else {
      toast.success(`${result.length} artículo(s) tarifados y añadidos a la nota`);
    }
  }, [tempBasket, addDays, storePacks, storeTariffs]);

  const removeStagedAdd = (barcode) => {
    setStagedAdds((prev) => prev.filter((i) => i.barcode !== barcode));
  };

  // ──────────────────────────────────────────────────────────────────────────
  // SUPLEMENTO: Stage
  // ──────────────────────────────────────────────────────────────────────────
  const addSupplement = () => {
    const amt = parseFloat(supplAmount);
    if (!amt || amt <= 0) { toast.error("Introduce un importe válido"); return; }
    if (!supplComment.trim()) { toast.error("Introduce un comentario"); return; }
    const id = `S${Date.now()}`;
    setSupplements((prev) => [...prev, { id, amount: amt, comment: supplComment.trim() }]);
    setSupplAmount("");
    setSupplComment("");
  };

  // ──────────────────────────────────────────────────────────────────────────
  // CONFIRMATION
  // ──────────────────────────────────────────────────────────────────────────
  const processConfirm = async () => {
    setConfirming(true);
    setStep("processing");
    try {
      const hdr = {};  // axios.defaults already carries Authorization from AuthContext

      // 1. Process swaps
      const swapsToProcess = workingItems.filter((i) => i.isSwapped && i.swapNewItem);
      for (const item of swapsToProcess) {
        const daysRem = Math.max(1, item.working_days);
        await axios.post(
          `${API}/rentals/${rental.id}/central-swap`,
          {
            old_item_barcode: item.barcode,
            new_item_barcode: item.swapNewItem.barcode,
            days_remaining: daysRem,
            payment_method: paymentMethod,
            delta_amount: item.swap_delta,
            break_pack: item.packBroken || false,
            pack_instance_id_to_break: item.packBroken ? item.pack_instance_id : null,
          }
        );
      }

      // 2. Process global date change / extension
      const origEndDate = (rental.end_date || "").split("T")[0];
      if (newEndDate && newEndDate !== origEndDate) {
        const start = new Date(rental.start_date);
        start.setHours(0, 0, 0, 0);
        const end = new Date(newEndDate + "T00:00:00");
        const newDays = Math.max(1, Math.round((end - start) / 86400000) + 1);
        const pendingBarcodes = workingItems
          .filter((i) => !i.isSwapped)
          .map((i) => i.barcode);
        if (pendingBarcodes.length > 0) {
          await axios.post(
            `${API}/rentals/${rental.id}/extend-items`,
            {
              barcodes: pendingBarcodes,
              new_end_date: newEndDate,
              new_days: newDays,
              payment_method: paymentMethod,
            }
          );
        }
      } else {
        // 2b. Individual item day/price changes (no global date change)
        // Include items where days changed OR price changed (e.g. pack-price recalculation
        // on same-day-count scenarios or reductions where item_days happened to already match)
        const changedItems = workingItems.filter(
          (i) => !i.isSwapped && (
            i.working_days !== i.item_days ||
            Math.abs((i.working_price || 0) - (i.unit_price || 0)) > 0.01
          )
        );
        if (changedItems.length > 0) {
          const totalDelta = changedItems.reduce(
            (s, i) => s + (i.working_price - i.unit_price), 0
          );
          await axios.post(
            `${API}/rentals/${rental.id}/update-item-prices`,
            {
              items: changedItems.map((i) => ({
                barcode: i.barcode,
                new_days: i.working_days,
                new_price: i.working_price,
              })),
              payment_method: paymentMethod,
              total_delta: Math.round(totalDelta * 100) / 100,
            }
          );
        }
      }

      // 3. Add new items
      if (stagedAdds.length > 0) {
        const totalAdd = stagedAdds.reduce((s, i) => s + i.working_price, 0);
        await axios.post(
          `${API}/rentals/${rental.id}/add-items`,
          {
            items: stagedAdds.map((i) => ({
              barcode: i.barcode,
              unit_price: i.working_price,
              person_name: "",
              pack_id: i.pack_id || null,
              pack_instance_id: i.pack_instance_id || null,
              pack_name: i.pack_name || null,
              item_days: i.working_days || addDays,
            })),
            days: addDays,
            end_date: newEndDate || origEndDate,
            charge_now: true,
            payment_method: paymentMethod,
            calculated_total: totalAdd,
          },
        );
      }

      // 4. Add supplements
      for (const suppl of supplements) {
        await axios.post(
          `${API}/rentals/${rental.id}/add-supplement`,
          {
            amount: suppl.amount,
            comment: suppl.comment,
            paid: true,
            payment_method: paymentMethod,
          }
        );
      }

      setStep("done");
    } catch (err) {
      const msg =
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Error al procesar los ajustes";
      toast.error(msg);
      setStep("payment");
    } finally {
      setConfirming(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // RENDER HELPERS
  // ──────────────────────────────────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const formatCreatedAt = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  // ──────────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────────
  if (!rental) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        // If we're already in the "done" state, closing the dialog should
        // trigger onSuccess so the parent list always refreshes — regardless
        // of whether the user clicks "Cerrar" or the X / ESC button.
        if (step === "done") {
          onSuccess();
        } else {
          onClose();
        }
      }
    }}>
      <DialogContent className="w-[90vw] max-w-[1200px] max-h-[94vh] overflow-hidden p-0 flex flex-col gap-0">
        {/* ── DONE STATE ── */}
        {step === "done" && (
          <div className="flex flex-col items-center justify-center h-64 gap-4 p-8">
            <CheckCircle className="h-16 w-16 text-emerald-500" />
            <h3 className="text-xl font-bold text-slate-900">¡Ajustes Confirmados!</h3>
            <p className="text-slate-500 text-sm">
              Todos los cambios han sido procesados correctamente.
            </p>
            <Button onClick={onSuccess} className="mt-2">Cerrar</Button>
          </div>
        )}

        {/* ── PAYMENT STEP ── */}
        {step === "payment" && (
          <div className="flex flex-col gap-6 p-8">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-slate-600" />
                Confirmar Ajustes — Método de Pago
              </DialogTitle>
            </DialogHeader>
            <div className={`rounded-xl p-4 border-2 ${diff > 0 ? "bg-orange-50 border-orange-300" : diff < 0 ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`}>
              <p className="text-sm text-slate-500 mb-1">
                {diff > 0 ? "Total a cobrar al cliente:" : diff < 0 ? "Total a devolver al cliente:" : "Sin diferencia económica"}
              </p>
              <p className={`text-3xl font-bold ${diff > 0 ? "text-orange-600" : diff < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                {diff >= 0 ? "+" : ""}{formatCurrency(diff)}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Método de pago / devolución:</Label>
              <div className="flex gap-2">
                {[
                  { id: "cash", icon: <Banknote className="h-4 w-4" />, label: "Efectivo" },
                  { id: "card", icon: <CreditCard className="h-4 w-4" />, label: "Tarjeta" },
                  { id: "pending", icon: <Clock className="h-4 w-4" />, label: "Pendiente" },
                ].map((m) => (
                  <Button
                    key={m.id}
                    variant={paymentMethod === m.id ? "default" : "outline"}
                    onClick={() => setPaymentMethod(m.id)}
                    className="flex-1 gap-1.5"
                  >
                    {m.icon} {m.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => setStep("editing")}>Revisar</Button>
              <Button
                onClick={processConfirm}
                disabled={confirming}
                className={diff > 0 ? "bg-orange-600 hover:bg-orange-700" : diff < 0 ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600"}
              >
                {confirming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                Confirmar y Procesar
              </Button>
            </div>
          </div>
        )}

        {/* ── PROCESSING STATE ── */}
        {step === "processing" && (
          <div className="flex flex-col items-center justify-center h-64 gap-4 p-8">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
            <p className="text-slate-600 font-medium">Procesando cambios...</p>
          </div>
        )}

        {/* ── EDITING STATE ── */}
        {step === "editing" && (
          <>
            {/* Hidden DialogTitle for accessibility */}
            <DialogHeader className="sr-only">
              <DialogTitle>Dashboard de Gestión — {rental?.customer_name}</DialogTitle>
            </DialogHeader>
            {/* ───────────────── HEADER ───────────────── */}
            <div className="shrink-0 border-b bg-white px-6 py-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                {/* Customer info */}
                <div className="flex items-start gap-3">
                  <SlidersHorizontal className="h-5 w-5 text-slate-500 mt-0.5 shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-bold text-slate-900">{rental.customer_name}</h2>
                      <Badge variant="outline" className="text-xs">{rental.customer_dni || "—"}</Badge>
                      {rental.customer_phone && (
                        <span className="text-sm text-slate-500">{rental.customer_phone}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Alta: {formatCreatedAt(rental.created_at || rental.start_date)} · #{rental.contract_number || rental.id?.slice(0, 6)}
                    </p>
                  </div>
                </div>

                {/* Global date range */}
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-2 border">
                  <CalendarDays className="h-4 w-4 text-slate-600 shrink-0" />
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500 font-medium">
                      {formatDate(rental.start_date)}
                    </span>
                    <ArrowRight className="h-3 w-3 text-slate-600" />
                    <div>
                      <Input
                        type="date"
                        value={newEndDate}
                        min={(rental.start_date || "").split("T")[0]}
                        onChange={(e) => handleGlobalEndDateChange(e.target.value)}
                        className="h-7 text-sm font-semibold border-0 bg-transparent p-0 focus-visible:ring-0 w-32"
                        data-testid="dashboard-global-end-date"
                      />
                      <p className="text-[10px] text-slate-600">
                        {workingItems[0]?.working_days || rental.days} días
                      </p>
                    </div>
                  </div>
                </div>

                {/* Historial toggle */}
                <Button
                  size="sm"
                  variant={showHistorial ? "default" : "outline"}
                  onClick={toggleHistorial}
                  className="h-8 text-xs gap-1.5"
                  data-testid="dashboard-historial-btn"
                >
                  <History className="h-3.5 w-3.5" />
                  Historial
                </Button>
              </div>
            </div>

            {/* ───────────────── MAIN AREA ───────────────── */}
            <div className={`flex-1 overflow-hidden min-h-0 grid ${showHistorial ? "grid-cols-[1fr_360px_280px]" : "grid-cols-[1fr_360px]"}`}>
              
              {/* ─── INVOICE PANEL (left) ─── */}
              <div className="overflow-y-auto border-r bg-slate-50/50 p-4 space-y-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                  Nota Original — {rental.contract_number || `#${rental.id?.slice(0, 6)}`}
                </p>

                {/* Pack rows */}
                {invoiceRows.map((row, idx) => {
                  if (row.type === "pack") {
                    const packDays = row.items[0]?.working_days || rental.days;
                    const packTotal = row.items.reduce((s, i) => {
                      return s + (i.isSwapped ? i.unit_price + (i.swap_delta || 0) : i.working_price);
                    }, 0);
                    const origPackTotal = row.items.reduce((s, i) => s + i.unit_price, 0);
                    const packChanged = Math.abs(packTotal - origPackTotal) > 0.01;
                    return (
                      <div key={`pack-${row.packInstanceId}`} className="bg-white rounded-lg border overflow-hidden shadow-sm">
                        {/* Pack header row */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border-b border-indigo-100">
                          <Package className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                          <span className="text-xs font-semibold text-indigo-700 flex-1">
                            Pack · {row.items.map((i) => i.item_type).join(" + ")}
                          </span>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={packDays}
                              min={1}
                              onChange={(e) => handlePackDaysChange(row.packInstanceId, e.target.value)}
                              className="w-14 h-6 text-xs text-center border-indigo-200"
                            />
                            <span className="text-[10px] text-indigo-500">días</span>
                            <span className={`text-sm font-bold ${packChanged ? "text-amber-600" : "text-slate-700"}`}>
                              {formatCurrency(packTotal)}
                              {packChanged && (
                                <span className="text-[10px] ml-1 text-slate-600">
                                  (orig. {formatCurrency(origPackTotal)})
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                        {/* Pack items */}
                        {row.items.map((item) => (
                          <div key={item._uid} className={`flex items-center gap-2 px-4 py-1.5 border-b last:border-0 text-xs ${item.isSwapped ? "bg-amber-50" : ""}`}>
                            <span className="font-mono text-blue-700 font-semibold w-20 truncate">
                              {item.internal_code || item.barcode?.slice(0, 8)}
                            </span>
                            <span className="text-slate-600 flex-1 truncate">{item.item_type}</span>
                            {item.size && <span className="text-slate-600">T.{item.size}</span>}
                            {item.isSwapped && (
                              <div className="flex items-center gap-1 ml-auto">
                                <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-300 h-4">
                                  → {item.swapNewItem?.internal_code || item.swapNewItem?.barcode}
                                </Badge>
                                <span className={`text-[10px] font-bold ${item.swap_delta > 0 ? "text-red-600" : item.swap_delta < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                                  {item.swap_delta >= 0 ? "+" : ""}{formatCurrency(item.swap_delta)}
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => cancelSwap(item._uid)}
                                  className="h-5 w-5 p-0 text-red-400 hover:text-red-600"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                            {item.packBroken && !item.isSwapped && (
                              <Badge className="text-[10px] bg-red-100 text-red-600 border-red-200 h-4 ml-auto">
                                Pack roto
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  }

                  // Individual item row
                  const item = row.item;
                  const changed = Math.abs(item.working_price - item.unit_price) > 0.01;
                  return (
                    <div
                      key={`item-${item._uid}`}
                      className={`flex items-center gap-2 bg-white rounded-lg border px-3 py-2 shadow-sm ${item.isSwapped ? "bg-amber-50 border-amber-200" : item.packBroken ? "bg-red-50 border-red-200" : ""}`}
                    >
                      <span className="font-mono text-blue-700 font-bold text-sm w-24 truncate">
                        {item.internal_code || item.barcode?.slice(0, 8)}
                      </span>
                      <span className="text-sm text-slate-600 flex-1 truncate">{item.item_type}</span>
                      {item.size && <span className="text-xs text-slate-600">T.{item.size}</span>}

                      {item.isSwapped ? (
                        <div className="flex items-center gap-1.5 ml-auto">
                          <ArrowRight className="h-3 w-3 text-amber-500" />
                          <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                            {item.swapNewItem?.internal_code || item.swapNewItem?.barcode}
                          </Badge>
                          <span className={`text-xs font-bold ${item.swap_delta > 0 ? "text-red-600" : item.swap_delta < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                            {item.swap_delta >= 0 ? "+" : ""}{formatCurrency(item.swap_delta)}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => cancelSwap(item._uid)}
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 ml-auto">
                          <Input
                            type="number"
                            value={item.working_days}
                            min={1}
                            onChange={(e) => handleItemDaysChange(item._uid, e.target.value)}
                            className="w-14 h-6 text-xs text-center"
                          />
                          <span className="text-[10px] text-slate-600">días</span>
                          <span className={`text-sm font-bold w-16 text-right ${changed ? "text-amber-600" : "text-slate-700"}`}>
                            {formatCurrency(item.working_price)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Added items — group pack items visually */}
                {(() => {
                  const seenPackIds = new Set();
                  const elements = [];
                  stagedAdds.forEach((item) => {
                    const pid = item.pack_instance_id;
                    if (pid && !seenPackIds.has(pid)) {
                      seenPackIds.add(pid);
                      const packItems = stagedAdds.filter((i) => i.pack_instance_id === pid);
                      const packTotal = packItems.reduce((s, i) => s + i.working_price, 0);
                      elements.push(
                        <div
                          key={`new-pack-${pid}`}
                          className="bg-emerald-50 border border-emerald-200 rounded-lg overflow-hidden shadow-sm"
                          data-testid={`staged-pack-${pid}`}
                        >
                          {/* Pack header */}
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 border-b border-emerald-200">
                            <Badge className="text-[10px] bg-emerald-600 text-white border-0 shrink-0 h-4">NUEVO</Badge>
                            <Package className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                            <span className="text-xs font-semibold text-emerald-700 flex-1 truncate">
                              {packItems[0]?.pack_name || packItems.map((i) => i.item_type).join(" + ")}
                            </span>
                            <span className="text-sm font-bold text-emerald-700 shrink-0">
                              +{formatCurrency(packTotal)}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setStagedAdds((prev) => prev.filter((i) => i.pack_instance_id !== pid))}
                              className="h-5 w-5 p-0 text-red-400 hover:text-red-600 shrink-0"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          {/* Pack items (pricing is shown only on the pack header, not per item) */}
                          {packItems.map((pItem) => (
                            <div
                              key={`add-pi-${pItem.barcode}`}
                              className="flex items-center gap-2 px-4 py-1 text-xs border-b last:border-0"
                            >
                              <span className="font-mono text-emerald-700 font-bold w-20 truncate">
                                {pItem.internal_code || pItem.barcode?.slice(0, 8)}
                              </span>
                              <span className="text-slate-600 flex-1 truncate">{pItem.item_type}</span>
                              {pItem.size && <span className="text-slate-600">T.{pItem.size}</span>}
                            </div>
                          ))}
                        </div>
                      );
                    } else if (!pid) {
                      elements.push(
                        <div
                          key={`add-${item.barcode}`}
                          className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 shadow-sm"
                          data-testid={`staged-add-${item.barcode}`}
                        >
                          <Badge className="text-[10px] bg-emerald-600 text-white border-0 shrink-0 h-4">NUEVO</Badge>
                          <span className="font-mono text-emerald-700 font-bold text-sm w-20 truncate">
                            {item.internal_code || item.barcode?.slice(0, 8)}
                          </span>
                          <span className="text-sm text-slate-600 flex-1 truncate">{item.item_type || item.name}</span>
                          {item.size && <span className="text-xs text-slate-600">T.{item.size}</span>}
                          <div className="flex items-center gap-1.5 ml-auto">
                            <span className="text-[10px] text-slate-600">{item.working_days}d</span>
                            <span className="text-sm font-bold text-emerald-700">
                              +{formatCurrency(item.working_price)}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeStagedAdd(item.barcode)}
                              className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    }
                  });
                  return elements;
                })()}

                {/* Supplements */}
                {supplements.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 shadow-sm"
                  >
                    <Badge className="text-[10px] bg-amber-500 text-white border-0 shrink-0 h-4">SUPL</Badge>
                    <span className="text-sm text-slate-700 flex-1 truncate">{s.comment}</span>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <span className="text-sm font-bold text-amber-700">+{formatCurrency(s.amount)}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSupplements((prev) => prev.filter((x) => x.id !== s.id))}
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}

                {invoiceRows.length === 0 && stagedAdds.length === 0 && supplements.length === 0 && (
                  <div className="text-center py-10 text-slate-600 text-sm">
                    No hay artículos activos en este alquiler
                  </div>
                )}
              </div>

              {/* ─── ACTIONS COLUMN (right) ─── */}
              <div className="overflow-y-auto p-4 space-y-3 bg-white">
                
                {/* CAMBIOS BLOCK */}
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-2">
                  <p className="text-xs font-bold text-orange-700 uppercase tracking-wider flex items-center gap-1">
                    <ArrowLeftRight className="h-3.5 w-3.5" /> Cambios de Material
                  </p>
                  <div className="space-y-1.5">
                    <div className="relative">
                      <Input
                        placeholder="Código artículo a devolver"
                        value={swapFromCode}
                        onChange={(e) => setSwapFromCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === "Enter") swapToRef.current?.focus(); }}
                        className="h-8 text-xs font-mono border-orange-200 focus:border-orange-400 bg-white"
                        data-testid="swap-from-input"
                      />
                    </div>
                    <div className="flex gap-1.5">
                      <div className="flex items-center justify-center w-6 shrink-0">
                        <ArrowRight className="h-3.5 w-3.5 text-orange-400" />
                      </div>
                      <Input
                        ref={swapToRef}
                        placeholder="Nuevo artículo (código)"
                        value={swapToCode}
                        onChange={(e) => setSwapToCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === "Enter") executeSwap(); }}
                        className="h-8 text-xs font-mono border-orange-200 focus:border-orange-400 bg-white flex-1"
                        data-testid="swap-to-input"
                      />
                      <Button
                        size="sm"
                        onClick={executeSwap}
                        disabled={swapLoading || !swapFromCode.trim() || !swapToCode.trim()}
                        className="h-8 bg-orange-500 hover:bg-orange-600 text-white text-xs px-3"
                        data-testid="swap-ok-btn"
                      >
                        {swapLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "OK"}
                      </Button>
                    </div>
                  </div>
                  {workingItems.some((i) => i.isSwapped) && (
                    <p className="text-[10px] text-orange-600">
                      {workingItems.filter((i) => i.isSwapped).length} cambio(s) pendiente(s)
                    </p>
                  )}
                </div>

                {/* AÑADIR BLOCK */}
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2">
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
                    <Plus className="h-3.5 w-3.5" /> Añadir Artículos
                  </p>
                  <div className="flex gap-1.5">
                    <Input
                      ref={addSearchRef}
                      placeholder="Escanear código o buscar..."
                      value={addSearch}
                      onChange={(e) => {
                        setAddSearch(e.target.value);
                        searchAddItems(e.target.value);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === "Enter" && addSearch.trim()) {
                          // Try direct barcode match
                          const results = addResults;
                          const exact = results.find(
                            (i) =>
                              i.barcode?.toUpperCase() === addSearch.toUpperCase() ||
                              i.internal_code?.toUpperCase() === addSearch.toUpperCase()
                          );
                          if (exact) addToTempBasket(exact);
                          else if (results.length === 1) addToTempBasket(results[0]);
                          else if (addSearch.length >= 3) await searchAddItems(addSearch);
                        }
                      }}
                      className="h-8 text-xs border-emerald-200 focus:border-emerald-400 bg-white flex-1"
                      data-testid="dashboard-add-search"
                    />
                    {addLoading && <Loader2 className="h-4 w-4 animate-spin text-emerald-500 self-center" />}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Label className="text-[10px] text-emerald-600 shrink-0">Días:</Label>
                    <Input
                      type="number"
                      value={addDays}
                      min={1}
                      onChange={(e) => setAddDays(parseInt(e.target.value) || 1)}
                      className="h-6 w-14 text-xs text-center border-emerald-200 bg-white"
                      data-testid="dashboard-add-days"
                    />
                    <span className="text-[10px] text-emerald-500 flex-1">días de alquiler</span>
                  </div>
                  {addResults.length > 0 && (
                    <div className="rounded border border-emerald-200 bg-white max-h-36 overflow-y-auto">
                      {addResults.map((item) => (
                        <button
                          key={item.barcode}
                          onClick={() => addToTempBasket(item)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-emerald-50 border-b last:border-0 text-left"
                        >
                          <span className="font-mono text-xs text-blue-700 font-bold w-16 truncate">
                            {item.internal_code || item.barcode}
                          </span>
                          <span className="text-xs text-slate-600 flex-1 truncate">
                            {item.name || item.item_type}
                            {item.size && ` (${item.size})`}
                          </span>
                          <Plus className="h-3 w-3 text-emerald-500 shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* ─── Cesta Temporal ─── */}
                  {tempBasket.length > 0 && (
                    <div className="space-y-1 pt-1.5 border-t border-emerald-300">
                      <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
                        <Search className="h-3 w-3" />
                        Cesta temporal ({tempBasket.length})
                      </p>
                      {tempBasket.map((item) => (
                        <div
                          key={item.barcode}
                          className="flex items-center gap-1.5 bg-white rounded border border-emerald-200 px-2 py-1"
                          data-testid={`basket-item-${item.barcode}`}
                        >
                          <span className="font-mono text-xs text-blue-700 font-bold w-16 truncate">
                            {item.internal_code || item.barcode?.slice(0, 8)}
                          </span>
                          <span className="text-xs text-slate-600 flex-1 truncate">
                            {item.item_type || item.name}
                          </span>
                          {item.size && (
                            <span className="text-[10px] text-slate-600">T.{item.size}</span>
                          )}
                          <button
                            onClick={() => removeFromTempBasket(item.barcode)}
                            className="p-0.5 text-red-400 hover:text-red-600 rounded shrink-0"
                            data-testid={`basket-remove-${item.barcode}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      <Button
                        size="sm"
                        onClick={commitBasket}
                        className="w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold mt-1 gap-1.5"
                        data-testid="basket-commit-btn"
                      >
                        <CheckCircle className="h-3.5 w-3.5" />
                        LISTO — Aplicar Tarifas y Packs
                      </Button>
                    </div>
                  )}

                  {stagedAdds.length > 0 && (
                    <p className="text-[10px] text-emerald-600 pt-1">
                      {stagedAdds.length} artículo(s) listos para añadir
                    </p>
                  )}
                </div>

                {/* SUPLEMENTO BLOCK */}
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1">
                    <Receipt className="h-3.5 w-3.5" /> Suplemento / Cargo Extra
                  </p>
                  <div className="flex gap-1.5">
                    <div className="relative w-24 shrink-0">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-600 text-xs">{getCurrencySymbol()}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={supplAmount}
                        onChange={(e) => setSupplAmount(e.target.value)}
                        className="h-8 text-xs pl-6 border-amber-200 bg-white"
                        data-testid="suppl-amount-input"
                      />
                    </div>
                    <Input
                      placeholder="Motivo (ej: rotura de bastón)"
                      value={supplComment}
                      onChange={(e) => setSupplComment(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") addSupplement(); }}
                      className="h-8 text-xs flex-1 border-amber-200 bg-white"
                      data-testid="suppl-comment-input"
                    />
                    <Button
                      size="sm"
                      onClick={addSupplement}
                      className="h-8 bg-amber-500 hover:bg-amber-600 text-white text-xs px-3 shrink-0"
                      data-testid="suppl-ok-btn"
                    >
                      OK
                    </Button>
                  </div>
                  {supplements.length > 0 && (
                    <p className="text-[10px] text-amber-600">
                      {supplements.length} suplemento(s) · +{formatCurrency(supplementsTotal)}
                    </p>
                  )}
                </div>

              </div>
            </div>

            {/* ───────────────── PANEL HISTORIAL (columna extra) ───────────────── */}
            {showHistorial && (
              <div className="overflow-y-auto border-l bg-slate-50 p-4">
                <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <History className="h-3.5 w-3.5" />
                  Historial de Cambios
                </p>
                {historialLoading ? (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-xs">Cargando...</span>
                  </div>
                ) : historialEvents.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs text-slate-600">Sin eventos registrados</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {historialEvents.map((ev) => (
                      <div
                        key={ev.id}
                        className="bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm"
                        data-testid={`history-event-${ev.id}`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <span className="text-xs font-semibold text-slate-700 flex-1 leading-tight">
                            {ev.description}
                          </span>
                          {ev.amount != null && Math.abs(ev.amount) > 0.01 && (
                            <span className={`text-xs font-bold shrink-0 ${ev.amount > 0 ? "text-emerald-600" : "text-red-500"}`}>
                              {ev.amount > 0 ? "+" : ""}{formatCurrency(Math.abs(ev.amount))}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-slate-600">
                            {ev.operator}
                          </span>
                          <span className="text-[10px] text-slate-300">·</span>
                          <span className="text-[10px] text-slate-600">
                            {new Date(ev.created_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ───────────────── FOOTER ───────────────── */}
            <div className="shrink-0 border-t bg-white px-6 py-4">
              <div className="flex items-center justify-between gap-4">
                {/* Original total */}
                <div className="bg-slate-100 rounded-lg px-4 py-2.5 text-center">
                  <p className="text-[10px] text-slate-500 uppercase font-medium">Precio Original</p>
                  <p className="text-xl font-bold text-slate-700">{formatCurrency(originalTotal)}</p>
                </div>

                {/* Diff */}
                <div className="flex items-center gap-2">
                  <ArrowRight className="h-5 w-5 text-slate-300" />
                  <div
                    className={`rounded-lg px-5 py-2.5 text-center min-w-[130px] border-2 ${
                      diff > 0
                        ? "bg-orange-50 border-orange-300"
                        : diff < 0
                        ? "bg-emerald-50 border-emerald-300"
                        : "bg-slate-50 border-slate-200"
                    }`}
                    data-testid="dashboard-diff"
                  >
                    <p className="text-[10px] uppercase font-medium text-slate-500">
                      {diff > 0 ? "A cobrar" : diff < 0 ? "A devolver" : "Sin diferencia"}
                    </p>
                    <p
                      className={`text-2xl font-bold ${
                        diff > 0 ? "text-orange-600" : diff < 0 ? "text-emerald-600" : "text-slate-600"
                      }`}
                    >
                      {diff >= 0 ? "+" : ""}{formatCurrency(diff)}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-auto">
                  <Button variant="outline" onClick={onClose}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => setStep("payment")}
                    disabled={!hasPendingChanges}
                    className="bg-blue-600 hover:bg-blue-700 text-white min-w-[200px]"
                    data-testid="dashboard-confirm-btn"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Confirmar Ajustes / Proceder a Caja
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
